import { useNavigate } from "@tanstack/react-router";
import { KeyRound, LogOut, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ErrorBox, PageHeader } from "../components/ui";
import { api, useSession } from "../lib/api";
import { dateTime, roleLabel } from "../lib/format";

export function AccountPage() {
  const session = useSession()!;
  const u = session.user;
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader title="Minha conta" />
      <div className="card divide-y divide-line">
        <Row label="Nome" value={u.display_name || "—"} />
        <Row label="E-mail" value={u.email} />
        <Row label="Papel" value={roleLabel[u.role]} />
        <Row
          label="Verificação em duas etapas"
          value={
            u.mfa_enabled ? (
              <span className="inline-flex items-center gap-1 text-ok">
                <ShieldCheck className="h-4 w-4" /> Ativa
              </span>
            ) : (
              <span className="text-muted">Desativada</span>
            )
          }
        />
        <Row label="Conta criada em" value={dateTime(u.created_at)} />
      </div>
      <section className="card p-5">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold">
          <KeyRound className="h-5 w-5 text-accent" /> Trocar senha
        </h2>
        <PasswordForm />
      </section>
      <button className="btn-ghost" onClick={() => void api.logout()}>
        <LogOut className="h-4 w-4" /> Sair
      </button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
      <span className="text-muted">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

/** Usada na conta e na troca obrigatória da senha provisória. */
function PasswordForm({ onDone }: { onDone?: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [ok, setOk] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== confirm) return;
    setBusy(true);
    setError(null);
    setOk(false);
    try {
      await api.changePassword(current, next);
      setOk(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      onDone?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="label" htmlFor="pw-current">
          Senha atual
        </label>
        <input
          id="pw-current"
          type="password"
          className="field"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="pw-new">
          Nova senha
        </label>
        <input
          id="pw-new"
          type="password"
          className="field"
          autoComplete="new-password"
          minLength={10}
          maxLength={128}
          required
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <p className="mt-1 text-xs text-muted">Pelo menos 10 caracteres. Uma frase longa é mais fácil de lembrar.</p>
      </div>
      <div>
        <label className="label" htmlFor="pw-confirm">
          Repita a nova senha
        </label>
        <input
          id="pw-confirm"
          type="password"
          className="field"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          aria-invalid={mismatch}
        />
        {mismatch && <p className="mt-1 text-xs text-danger">As senhas não são iguais.</p>}
      </div>
      {error ? <ErrorBox error={error} /> : null}
      {ok && <p className="text-sm text-ok">Senha trocada. As outras sessões foram encerradas.</p>}
      <button className="btn-primary" disabled={busy || mismatch}>
        {busy ? "Salvando…" : "Trocar senha"}
      </button>
    </form>
  );
}

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const session = useSession();
  return (
    <div className="mx-auto max-w-md">
      <PageHeader
        title="Crie sua senha"
        subtitle={
          session?.user.must_change_password
            ? "Você entrou com uma senha provisória. Escolha uma senha só sua para continuar."
            : "Troque sua senha."
        }
      />
      <div className="card p-5">
        <PasswordForm onDone={() => void navigate({ to: "/" })} />
      </div>
    </div>
  );
}
